import { Inject, Injectable } from '@angular/core';
import { PIPELINE_SETTINGS_CONFIG_TOKEN, PipelineSettingsConfig } from './tokens';
import { SalesPipelinesService, PipelineInterface, StageInterface } from '@vendasta/sales-opportunities';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import { TranslateService } from '@ngx-translate/core';

@Injectable()
export class PipelineSettingsService {
  readonly partnerId$: Observable<string>;
  readonly currentPipeline$$ = new BehaviorSubject<PipelineInterface>({});
  readonly currentPipeline$ = this.currentPipeline$$.asObservable();
  private readonly redirectRequests$$ = new BehaviorSubject<boolean>(false);
  readonly redirectRequests$ = this.redirectRequests$$.asObservable();

  private readonly defaultPipelineName = this.translateService.instant(
    'FRONTEND.SALES_UI.PIPELINE.SETTINGS.DEFAULTS.NAME',
  );
  private readonly defaultPipelineStages: StageInterface[] = [
    {
      name: this.translateService.instant('FRONTEND.SALES_UI.PIPELINE.SETTINGS.DEFAULTS.STAGE_ONE'),
      probability: 0.1,
    },
    {
      name: this.translateService.instant('FRONTEND.SALES_UI.PIPELINE.SETTINGS.DEFAULTS.STAGE_TWO'),
      probability: 0.3,
    },
    {
      name: this.translateService.instant('FRONTEND.SALES_UI.PIPELINE.SETTINGS.DEFAULTS.STAGE_THREE'),
      probability: 0.99,
    },
  ];

  constructor(
    @Inject(PIPELINE_SETTINGS_CONFIG_TOKEN) private readonly config: PipelineSettingsConfig,
    private readonly salesPipelinesService: SalesPipelinesService,
    private translateService: TranslateService,
  ) {
    this.partnerId$ = this.config.partnerId$;
  }

  list(partnerId: string): Observable<PipelineInterface[]> {
    return this.salesPipelinesService.list(partnerId);
  }

  updatePipelineName(pipelineId: string, pipelineName: string): Observable<boolean> {
    if (!pipelineId || !pipelineName) {
      return of(false);
    }
    return this.partnerId$.pipe(
      switchMap((partnerId) => this.salesPipelinesService.updatePipelineName(partnerId, pipelineId, pipelineName)),
      map((_) => {
        return true;
      }),
      catchError(() => of(false)),
    );
  }

  deletePipeline(pipelineId: string): Observable<boolean> {
    if (!pipelineId) {
      return of(false);
    }
    return this.partnerId$.pipe(
      switchMap((partnerId) => this.salesPipelinesService.deletePipeline(partnerId, pipelineId)),
      map((_) => {
        return true;
      }),
      catchError(() => of(false)),
    );
  }

  createDefaultPipeline(): Observable<string> {
    return this.partnerId$.pipe(
      switchMap((partnerId) =>
        this.salesPipelinesService.createPipeline(partnerId, this.defaultPipelineName, this.defaultPipelineStages),
      ),
      map((resp) => {
        if (resp?.pipelineId) {
          return resp.pipelineId;
        } else {
          return '';
        }
      }),
      catchError(() => of('')),
    );
  }

  deleteStage(pipelineId: string, stageId: string): Observable<boolean> {
    if (!stageId || !pipelineId) {
      return of(false);
    }
    return this.partnerId$.pipe(
      switchMap((partnerId) => this.salesPipelinesService.removePipelineStages(partnerId, pipelineId, [stageId])),
      map((_) => {
        return true;
      }),
      catchError(() => of(false)),
    );
  }

  updateStageName(pipelineId: string, stageId: string, stageName: string): Observable<boolean> {
    if (!stageId || !pipelineId || !stageName) {
      return of(false);
    }
    return this.partnerId$.pipe(
      switchMap((partnerId) =>
        this.salesPipelinesService.updatePipelineStageName(partnerId, pipelineId, stageId, stageName),
      ),
      map((_) => {
        return true;
      }),
      catchError(() => of(false)),
    );
  }

  updateStageProbability(pipelineId: string, stageId: string, probability: number): Observable<boolean> {
    if (!stageId || !pipelineId || probability < 0 || 1 < probability) {
      return of(false);
    }
    return this.partnerId$.pipe(
      switchMap((partnerId) =>
        this.salesPipelinesService.updatePipelineStageProbability(partnerId, pipelineId, stageId, probability),
      ),
      map((_) => {
        return true;
      }),
      catchError(() => of(false)),
    );
  }
  addStages(pipelineId: string, stages: StageInterface[]): Observable<boolean> {
    return this.partnerId$.pipe(
      switchMap((partnerId) => {
        const newStages: any[] = stages.map((stage) => {
          return {
            name: stage.name,
            probability: stage.probability,
          };
        });
        return this.salesPipelinesService.addPipelineStages(partnerId, pipelineId, newStages);
      }),
      map((_) => {
        return true;
      }),
      catchError(() => of(false)),
    );
  }

  requestReload(): void {
    this.redirectRequests$$.next(true);
  }
}
